import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, Truck, ChevronDown, CheckCircle, XCircle } from 'lucide-react';

const NeighborhoodSelector = ({ zones, selectedZone, onSelect, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Mostrar busca apenas se tiver 15+ bairros
  const showSearchField = zones.length >= 15;
  
  // Filtrar bairros pela busca (case-insensitive, sem acentos)
  const normalizeText = (text) => {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };
  
  const filteredZones = useMemo(() => {
    if (!searchTerm.trim()) return zones;
    const normalized = normalizeText(searchTerm);
    return zones.filter(zone => normalizeText(zone.name).includes(normalized));
  }, [zones, searchTerm]);
  
  // Ordenar alfabeticamente
  const sortedZones = useMemo(() => {
    return [...filteredZones].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [filteredZones]);
  
  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // NÃO focar automaticamente no input - deixar usuário ver a lista primeiro
  // O usuário pode tocar no campo de busca se quiser digitar
  
  // Destacar texto que combina com a busca
  const highlightMatch = (text, search) => {
    if (!search.trim()) return text;
    const normalizedSearch = normalizeText(search);
    const normalizedText = normalizeText(text);
    const index = normalizedText.indexOf(normalizedSearch);
    
    if (index === -1) return text;
    
    const beforeMatch = text.slice(0, index);
    const match = text.slice(index, index + search.length);
    const afterMatch = text.slice(index + search.length);
    
    return (
      <>
        {beforeMatch}
        <span className="bg-yellow-200 text-yellow-900 font-semibold rounded px-0.5">{match}</span>
        {afterMatch}
      </>
    );
  };
  
  // Se não há bairros cadastrados
  if (zones.length === 0) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-red-500 text-lg">⚠️</span>
          <div>
            <p className="text-sm font-medium text-red-700">Entrega indisponível</p>
            <p className="text-xs text-red-600">Este estabelecimento ainda não cadastrou bairros para entrega.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
      
      {/* Botão de seleção */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-3 border rounded-xl text-left flex items-center justify-between transition-all ${
          error ? 'border-red-400 bg-red-50' : 
          selectedZone ? 'border-green-400 bg-green-50' : 
          'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectedZone ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">{selectedZone.name}</span>
              <span className="ml-auto flex-shrink-0 bg-gray-100 text-gray-700 text-sm font-medium px-2 py-0.5 rounded-full">
                R$ {selectedZone.fee.toFixed(2).replace('.', ',')}
              </span>
            </>
          ) : (
            <>
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-gray-500">Selecione seu bairro</span>
            </>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Taxa de entrega quando selecionado */}
      {selectedZone && !error && !isOpen && (
        <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
          <Truck className="w-3 h-3" />
          Taxa de entrega: R$ {selectedZone.fee.toFixed(2).replace('.', ',')}
        </p>
      )}
      
      {/* Mensagem de erro */}
      {error && (
        <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          {error}
        </p>
      )}
      
      {/* Dropdown com lista de bairros */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header com busca (somente se tiver 15+ bairros) */}
          <div className="sticky top-0 bg-white border-b border-gray-100 p-3">
            {showSearchField ? (
              <>
                {/* Barra de busca - NÃO foca automaticamente */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    inputMode="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Toque para buscar..."
                    className="w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>
                {/* Contador de resultados */}
                <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                  <span>
                    {searchTerm ? (
                      sortedZones.length === 0 ? 'Nenhum bairro encontrado' : 
                      `${sortedZones.length} bairro${sortedZones.length !== 1 ? 's' : ''} encontrado${sortedZones.length !== 1 ? 's' : ''}`
                    ) : (
                      `${zones.length} bairro${zones.length !== 1 ? 's' : ''} disponíve${zones.length !== 1 ? 'is' : 'l'}`
                    )}
                  </span>
                  {selectedZone && (
                    <button 
                      type="button"
                      onClick={() => { onSelect(null); setIsOpen(false); }}
                      className="text-red-500 hover:text-red-700 font-medium"
                    >
                      Limpar
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* Se poucos bairros, mostrar apenas o contador e botão limpar */
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span className="font-medium text-gray-700">
                  {zones.length} bairro{zones.length !== 1 ? 's' : ''} disponíve{zones.length !== 1 ? 'is' : 'l'}
                </span>
                {selectedZone && (
                  <button 
                    type="button"
                    onClick={() => { onSelect(null); setIsOpen(false); }}
                    className="text-red-500 hover:text-red-700 font-medium"
                  >
                    Limpar
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Lista de bairros */}
          <div className="max-h-64 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {sortedZones.length === 0 ? (
              <div className="p-6 text-center">
                <div className="text-gray-400 mb-2">
                  <Search className="w-8 h-8 mx-auto opacity-50" />
                </div>
                <p className="text-gray-500 text-sm">Nenhum bairro encontrado</p>
                <p className="text-gray-400 text-xs mt-1">Tente buscar por outro nome</p>
              </div>
            ) : (
              <div className="py-1">
                {sortedZones.map((zone, index) => {
                  const isSelected = selectedZone?.id === zone.id;
                  return (
                    <button
                      key={zone.id}
                      type="button"
                      onClick={() => {
                        onSelect(zone);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`w-full px-4 py-3 flex items-center justify-between transition-all ${
                        isSelected 
                          ? 'bg-green-50 border-l-4 border-green-500' 
                          : 'hover:bg-gray-50 border-l-4 border-transparent'
                      } ${index !== sortedZones.length - 1 ? 'border-b border-gray-50' : ''}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Indicador de seleção */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected 
                            ? 'border-green-500 bg-green-500' 
                            : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        {/* Nome do bairro */}
                        <span className={`text-sm truncate ${isSelected ? 'font-semibold text-green-800' : 'text-gray-700'}`}>
                          {highlightMatch(zone.name, searchTerm)}
                        </span>
                      </div>
                      {/* Badge de preço */}
                      <span className={`flex-shrink-0 text-sm font-medium px-2.5 py-1 rounded-full ${
                        isSelected 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        R$ {zone.fee.toFixed(2).replace('.', ',')}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPONENTE: ProductSkeleton
// Skeleton loader para produtos (mostra enquanto carrega)
// ============================================================

export default NeighborhoodSelector;
